import type {
  AutoTradeDrawerParams,
  GamePlayerProjectionScore,
  SaveScoringRequest,
  ScoringSort
} from '@library/types/scoring';
import type {
  GamePlayerProjectionsAndScoresRequest,
  Status
} from '@library/types/projection';
import {
  getBooleanParameterValue,
  getParameterValue
} from '@library/component-utils/filters';
import { useEffect, useMemo, useState } from 'react';
import {
  useGetGamePlayerProjectionScoreQuery,
  useSaveScoringMutation
} from '@bo-services/scoringApi';
import type { LeagueInfo } from '@library/types/leagues';
import { Loading } from '@library/components/Loading';
import { NoResults } from '@library/components/NoResults';
import { PaginationButtons } from '@library/components/buttons';
import { ScoringHeader } from '@library/components/scoring';
import { ScoringRows } from '@bo-components/scoring/ScoringRows';
import { SubmitResetBar } from '@library/components/SubmitResetBar';
import { findProjectionByProjectionId } from '@bo-utils/projection';
import { getModifiedProjections } from '@bo-utils/scoring';
import { hasStandAloneOwnAttributes } from '@bo-utils/leagueAttributes';
import { selectLoggedInUserId } from '@bo-services/authenticationSlice';
import { useImmer } from 'use-immer';
import { usePublishProjectionsMutation } from '@bo-services/projectionsApi';
import { useSelector } from 'react-redux';

export interface ScoringProps {
  currentPage: number;
  disabledFilters: boolean;
  dnp: string;
  durationId: Array<string>;
  fromDate: string;
  gameModeIds: Array<number>;
  games: Array<string>;
  handleShowHistoryDrawer: (params: AutoTradeDrawerParams) => void;

  leagueId: Array<string>;
  leagueInfo?: LeagueInfo;
  perPage: string;
  statTypeIds: Array<string>;
  status: Array<string>;
  searchValue: string;
  setCurrentPage: (page: number) => void;
  setDisabledFilters: (disableFilters: boolean) => void;
  teams: Array<string>;
  toDate?: string;
  reboot: string;
}

export const Scoring = ({
  currentPage,
  disabledFilters,
  dnp,
  durationId,
  fromDate,
  gameModeIds,
  games,
  handleShowHistoryDrawer,
  leagueId,
  leagueInfo = undefined,
  perPage,
  statTypeIds,
  status,
  searchValue,
  setDisabledFilters,
  setCurrentPage,
  teams,
  toDate = undefined,
  reboot
}: ScoringProps) => {
  const [sort, setSort] = useState<ScoringSort>({
    sortBy: 'start_time',
    sortDirection: 'asc'
  });

  const { name } = leagueInfo || { name: '' };

  const standAloneAttributes = hasStandAloneOwnAttributes({ name });
  const loggedInUserId = useSelector(selectLoggedInUserId);

  const scoringQueryParams =
    useMemo<GamePlayerProjectionsAndScoresRequest>(() => {
      return {
        createdByUserId: window.backoffice?.isAutomatedTesting
          ? Number(loggedInUserId)
          : undefined,
        durationId: standAloneAttributes ? durationId : [],
        fromDate,
        gameId: getParameterValue(games),
        gameModeIds,
        isOffTheBoard: getBooleanParameterValue(dnp),
        leagueId,
        perPage: Number(perPage),
        reboot: getBooleanParameterValue(reboot),
        search: searchValue,
        sortBy: sort.sortBy,
        sortDirection: sort.sortDirection,
        statTypeIds: getParameterValue(statTypeIds),
        status: getParameterValue<Array<Status>>(status),
        team: getParameterValue(teams),
        toDate
      };
    }, [
      dnp,
      durationId,
      fromDate,
      leagueId,
      loggedInUserId,
      gameModeIds,
      games,
      perPage,
      reboot,
      sort,
      statTypeIds,
      status,
      searchValue,
      standAloneAttributes,
      teams,
      toDate
    ]);

  const {
    data: scoresData,
    isLoading,
    isFetching,
    isError
  } = useGetGamePlayerProjectionScoreQuery(
    {
      ...scoringQueryParams,
      page: currentPage
    },
    { skip: leagueId.length === 0 }
  );

  const [changes, setChanges] = useImmer<
    Array<Partial<GamePlayerProjectionScore>>
  >([]);

  const updateProjection = ({
    change,
    initialProjection
  }: {
    change: Partial<GamePlayerProjectionScore>;
    initialProjection: GamePlayerProjectionScore;
  }) => {
    const {
      autoFinalize,
      isOffTheBoard,
      isFinal,
      score,
      startTime,
      status: initialStatus,
      projectionId,
      projectionValue,
      displayTime
    } = initialProjection;
    const {
      autoFinalize: changedAutoFinalize,
      isOffTheBoard: changedIsOffTheBoard,
      isFinal: changedIsFinal,
      projectionValue: changedProjectionValue,
      startTime: changedStartTime,
      status: changedStatus,
      score: changedScore,
      displayTime: changedDisplayTime
    } = change;

    const hasNoChanges = (
      allChanges: Array<{
        changedValue: unknown;
        originalValue: unknown;
      }>
    ) => {
      return allChanges.every(
        ({ changedValue, originalValue }) =>
          changedValue === undefined || changedValue === originalValue
      );
    };

    setChanges((draft) => {
      const affectedProjectionIndex = draft.findIndex(
        (projection) => projection.projectionId === projectionId
      );

      if (affectedProjectionIndex === -1) {
        draft.push({
          projectionId,
          ...change
        });

        return;
      }

      const noChanges = hasNoChanges([
        { changedValue: changedAutoFinalize, originalValue: autoFinalize },
        {
          changedValue: changedProjectionValue,
          originalValue: projectionValue
        },
        { changedValue: changedIsOffTheBoard, originalValue: isOffTheBoard },
        { changedValue: changedIsFinal, originalValue: isFinal },
        { changedValue: changedScore, originalValue: score },
        { changedValue: changedStartTime, originalValue: startTime },
        { changedValue: changedStatus, originalValue: initialStatus },
        { changedValue: changedDisplayTime, originalValue: displayTime }
      ]);

      if (noChanges) {
        draft.splice(affectedProjectionIndex, 1);
      } else {
        draft[affectedProjectionIndex] = {
          ...draft[affectedProjectionIndex],
          ...change
        };
      }
    });
  };

  useEffect(() => {
    setChanges([]);
  }, [scoresData, setChanges]);

  const updatedScoresList = getModifiedProjections({
    initialProjections: scoresData?.scores ?? [],
    updatedProjections: changes
  });

  const totalChanges = updatedScoresList.length;

  useEffect(() => {
    const hasChanges = !!totalChanges;

    if (disabledFilters !== hasChanges) {
      setDisabledFilters(hasChanges);
    }
  }, [disabledFilters, setDisabledFilters, totalChanges]);

  const showSubmitResetBar = totalChanges > 0;

  const [saveScoring] = useSaveScoringMutation();
  const [publishProjections] = usePublishProjectionsMutation();

  const handleSaveScoring = async () => {
    const saveScoreRequest: SaveScoringRequest = {
      scoring_request: updatedScoresList.flatMap((updatedScore) => {
        const { autoFinalize, isFinal, isOffTheBoard, projectionId, score } =
          updatedScore;

        const relevantChange = findProjectionByProjectionId({
          projectionArray: changes,
          projectionId
        });

        if (
          relevantChange?.autoFinalize ||
          relevantChange?.score ||
          relevantChange?.isFinal ||
          relevantChange?.isOffTheBoard ||
          relevantChange?.gameModeIds
        ) {
          return [
            {
              projection_id: projectionId,
              score: score || 0,
              is_off_the_board: isOffTheBoard,
              is_final: isFinal,
              auto_finalize: autoFinalize
            }
          ];
        }

        return [];
      })
    };

    const createUpdateProjections = updatedScoresList.flatMap((projection) => {
      const {
        autoTrade,
        boardTime,
        description,
        gameId,
        inGame,
        maxProbabilityOfMore,
        minProbabilityOfMore,
        playerId: projectionPlayerId,
        projectionId,
        projectionValue,
        rank,
        startTime,
        displayTime,
        status: newStatus,
        targetProbabilityOfMore
      } = projection;

      const relevantChange = findProjectionByProjectionId({
        projectionArray: changes,
        projectionId
      });

      if (relevantChange?.status || relevantChange?.startTime) {
        return [
          {
            players: [{ id: Number(projectionPlayerId) }],
            projections: [
              {
                id: projectionId ? Number(projectionId) : null,
                auto_trade: autoTrade,
                board_time: boardTime,
                description,
                duration_id: Number(durationId),
                game_id: gameId,
                game_mode_ids: gameModeIds,
                in_game: inGame,
                league_id: Number(leagueId),
                line_score: String(projectionValue),
                max_probability_of_more: maxProbabilityOfMore,
                min_probability_of_more: minProbabilityOfMore,
                rank: rank ? Number(rank) : 0,
                start_time: startTime,
                display_time: displayTime,
                stat_type_id: Number(projection.statTypeId),
                status: newStatus,
                target_probability_of_more: targetProbabilityOfMore
              }
            ]
          }
        ];
      }
      return [];
    });

    if (createUpdateProjections.length > 0) {
      await publishProjections({
        projections_request: createUpdateProjections
      });
    }

    if (saveScoreRequest.scoring_request.length > 0) {
      await saveScoring(saveScoreRequest);
    }
  };

  if (isLoading || isFetching) {
    return <Loading loadingMessage="Loading Leagues" />;
  }

  if (isError || !scoresData?.scores?.length) {
    return <NoResults label="No Projections Found" />;
  }

  return (
    <div className="border-soFresh-140 overflow-hidden rounded-lg border">
      {showSubmitResetBar && (
        <SubmitResetBar
          handlePublish={handleSaveScoring}
          handleReset={() => setChanges([])}
        />
      )}
      <div className="overflow-x-scroll">
        <table className="w-full">
          <ScoringHeader handleSort={setSort} sort={sort} />
          <ScoringRows
            changes={changes}
            handleShowHistoryDrawer={handleShowHistoryDrawer}
            initialScoresData={scoresData.scores}
            leagueInfo={leagueInfo}
            updateProjection={updateProjection}
          />
        </table>
      </div>

      <PaginationButtons
        currentPage={currentPage}
        handlePageChange={setCurrentPage}
        totalPages={scoresData?.paginationInfo.total_pages ?? 0}
      />
    </div>
  );
};
